import React, { useState, useEffect, useCallback } from 'react';
import claudeAPIService from '../../../services/claudeAPI';
import { getRayDisplayName, getTypeDisplayName, getElementDisplayName } from '../../../constants/oracleMysticalMappings';
import './OracleOutput.css';

const OracleOutput = ({ sessionData, outputType, title, onComplete, onBack, updateSession }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const getExistingContent = useCallback(() => {
    switch (outputType) {
      case 'blockage':
        return sessionData.blockageText;
      case 'solution':
        return sessionData.unblockText;
      case 'wisdom':
        return sessionData.wisdomText;
      case 'decree':
        return sessionData.decreeText;
      default:
        return '';
    }
  }, [outputType, sessionData]);

  const getAPICallType = () => {
    switch (outputType) {
      case 'blockage':
        return 'blockage';
      case 'solution':
        return 'solution';
      case 'wisdom':
        return 'wisdom';
      case 'decree':
        return 'decree';
      default:
        return 'blockage';
    }
  };

  const getAPIVariables = () => {
    if (outputType === 'blockage') {
      return {
        raySelection: sessionData.raySelection,
        userQuestion: sessionData.userQuestion,
        diagnosisType: sessionData.diagnosisType,
        diagnosisElement: sessionData.diagnosisElement
      };
    } else if (outputType === 'wisdom') {
      return {
        raySelection: sessionData.raySelection,
        solutionType: sessionData.solutionType,
        solutionElement: sessionData.solutionElement
      };
    } else {
      // solution and decree use same variables
      return {
        raySelection: sessionData.raySelection,
        userQuestion: sessionData.userQuestion,
        solutionType: sessionData.solutionType,
        solutionElement: sessionData.solutionElement
      };
    }
  };

  const generateOracleContent = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Single API call for each output type
      const variables = getAPIVariables();
      const apiCallType = getAPICallType();
      
      const generatedContent = await claudeAPIService.executeAPICall(
        apiCallType,
        variables,
        sessionData.sessionId
      );

      setContent(generatedContent);

      // Update session with generated content
      const sessionUpdate = {};
      if (outputType === 'blockage') {
        sessionUpdate.blockageText = generatedContent;
      } else if (outputType === 'solution') {
        sessionUpdate.unblockText = generatedContent;
      } else if (outputType === 'wisdom') {
        sessionUpdate.wisdomText = generatedContent;
      } else if (outputType === 'decree') {
        sessionUpdate.decreeText = generatedContent;
      }
      
      await updateSession(sessionUpdate);

    } catch (err) {
      setError('Erro ao consultar o oráculo. Tente novamente.');
      console.error('Error generating oracle content:', err);
    } finally {
      setLoading(false);
    }
  }, [outputType, sessionData, updateSession]);

  useEffect(() => {
    // Complete reset when component mounts or outputType changes
    setIsProcessing(false);
    setLoading(false);
    setError('');
    
    // Check if content already exists in session
    const existingContent = getExistingContent();
    if (existingContent) {
      setContent(existingContent);
    } else {
      // Prevent duplicate API calls - only call if we don't have content
      if (!content && !loading) {
        generateOracleContent();
      }
    }
  }, [outputType]); // Removed dependencies that cause re-runs

  // Reset processing state when content is loaded
  useEffect(() => {
    if (content && !loading && !error) {
      setIsProcessing(false);
    }
  }, [content, loading, error]);


  const getLoadingMessage = () => {
    switch (outputType) {
      case 'blockage':
        return 'Revelando seu bloqueio...';
      case 'solution':
        return 'Descobrindo sua solução...';
      case 'wisdom':
        return 'Canalizando sabedoria ancestral...';
      case 'decree':
        return 'Forjando seu decreto pessoal...';
      default:
        return 'Consultando o Oráculo dos 12 Raios...';
    }
  };

  const getSelectionSummary = () => {
    if (outputType === 'blockage') {
      return {
        title: 'Diagnóstico',
        selections: [
          { label: '', value: getRayDisplayName(sessionData.raySelection) },
          { label: '', value: getTypeDisplayName(sessionData.diagnosisType) },
          { label: 'Elemento', value: getElementDisplayName(sessionData.diagnosisElement) }
        ]
      };
    } else if (outputType === 'solution' || outputType === 'decree') {
      return {
        title: 'Solução',
        selections: [
          { label: '', value: getRayDisplayName(sessionData.raySelection) },
          { label: '', value: getTypeDisplayName(sessionData.solutionType) },
          { label: 'Elemento', value: getElementDisplayName(sessionData.solutionElement) }
        ]
      };
    } else if (outputType === 'wisdom') {
      return {
        title: 'Sabedoria',
        selections: [
          { label: '', value: getRayDisplayName(sessionData.raySelection) },
          { label: '', value: getTypeDisplayName(sessionData.solutionType) },
          { label: 'Elemento', value: getElementDisplayName(sessionData.solutionElement) }
        ]
      };
    } else {
      return null;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="oracle-loading">
          <div className="oracle-loading-animation">
            <div className="loading-orb"></div>
            <div className="loading-waves">
              <div className="wave wave-1"></div>
              <div className="wave wave-2"></div>
              <div className="wave wave-3"></div>
            </div>
          </div>
          <p className="loading-message">{getLoadingMessage()}</p>
          <small>Conectando com as energias universais...</small>
        </div>
      );
    }

    if (error) {
      return (
        <div className="oracle-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={generateOracleContent} className="retry-btn primary-cta">
            Consultar Novamente
          </button>
        </div>
      );
    }

    if (typeof content === 'string' && content) {
      return (
        <div className="oracle-content">
          {outputType === 'decree' ? (
            <div className="decree-display">
              <div className="oracle-text decree-text">
                "{content}"
              </div>
              <small>Repita este decreto diariamente para fortalecer sua transformação</small>
            </div>
          ) : (
            <div className="oracle-text">
              {content}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const handleContinue = () => {
    if (!isProcessing && content) {
      setIsProcessing(true);
      onComplete();
    }
  };

  const selectionSummary = getSelectionSummary();

  return (
    <div className="oracle-step oracle-output-step">
      <h2>{title}</h2>

      {selectionSummary && (
        <div className="selection-summary">
          <h4>{selectionSummary.title}</h4>
          <div className="selection-display">
            {selectionSummary.selections.map((selection, index) => (
              <span key={index} className="selection-item">
                {selection.label} {selection.value}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="oracle-output-container">
        {renderContent()}
      </div>

      {content && !loading && !error && (
        <div className="step-navigation">
          <button 
            onClick={handleContinue} 
            className="continue-btn primary-cta"
            disabled={isProcessing}
            style={{ margin: '0 auto' }}
          >
            {isProcessing ? 'Processando...' : (outputType === 'decree' ? 'Ver Resumo Final' : 'Continuar →')}
          </button>
        </div>
      )}
    </div>
  );
};

export default OracleOutput;